// Stock/default photo URL detection.
//
// Stock is decided by PROVENANCE — the path Joy serves the asset from — never by what the host
// named their file. A filename heuristic used to live here (`/^classic_wedding_/`, `/-unsplash/`
// tested against the basename) and it dropped real uploads out of messages: Joy media URLs carry
// the upload's original filename in their last path segment, and unsplash.com names every download
// `<photographer>-<id>-unsplash.jpg`. Any host who downloaded a photo from Unsplash and uploaded it
// had it silently stripped from their message. Neither pattern had a target the rules below miss —
// `classic_wedding_` existed for one fixed seed URL, now matched exactly in STOCK_PATHNAMES.
//
// Matching is on `URL.pathname`, not the full URL. Asset paths are deterministic across
// environments; hosts are not, so a host-locked prefix (`https://withjoy.com/...`) silently misses
// stock art on `dev.withjoy.com` and staging.
//
// Third copy of this logic. `event_service/src/models/event/availablePhotos.ts` is the reference
// implementation; `contact_service/src/utils/stock-photo-url.ts` does the actual strip at send
// time. Keep all three in step. Consolidating them needs a shared package.

// Only `pathname` and `searchParams` are read below, so the base is arbitrary — it exists solely
// to let `new URL` resolve a root-relative value ('/assets/public/...'). Without it those throw,
// the catch swallows them as "not stock", and a stock photo passed as a path sends.
const RELATIVE_URL_BASE = 'https://withjoy.com';

const STOCK_PATHNAME_PREFIXES: ReadonlyArray<string> = [
  '/assets/public/wedding-website/designs-gallery/default-assets/',
  // Every `defaultHeaderPhoto` in src/shared/websiteTemplates/data lives under this leaf. Sibling
  // directories (brannan_thumbnails/, aloha_thumbnails/) are template-picker card art, never header
  // photos, so the prefix stops here rather than at gallery_thumbnails/.
  '/assets/public/marcom-prod/wedding-website-gallery/gallery_thumbnails/default_preview_images/',
  '/assets/public/defaultwebsitephotos/',
  '/assets/public/createwedding/seed-wedding-templates/',
  // Seed event photos — SEED_WEDDING_EVENT_PHOTO_URL / SEED_GENERAL_EVENT_PHOTO_URL in
  // WebsitePhotoV2.tsx and WebsitePreviewPhoto.tsx.
  '/assets/public/event-photo/',
];

// Known stock images that live outside the asset prefixes. Pathnames only, origin-agnostic, for
// the same cross-environment reason as the prefixes above.
const STOCK_PATHNAMES: ReadonlyArray<string> = [
  '/media/ea08b6c9814abe8d85f1f6999d73bd9afb9536716d12561b3/Kyhc6muKTs6gtn2hF7t7_w_couple_marshall_lily_kissing_hidden_underpass%20copy.jpg',
  // The header photo seeded into every new wedding event
  // (event_service `config/event-types/wedding.default.json`).
  '/media/e89d41130d57a3d083650edbbffa8bad2b9185d284db6bb3f/classic_wedding_info_1717740693.jpeg',
  // The built-in fallback every message composer seeds the photo field with when the event has no
  // real photo (`PLACEHOLDER_PHOTO_URL` in GeneralProvider / RsvpReminderProvider /
  // HotelReminderProvider). Changing the placeholder means adding the new pathname here AND in
  // contact_service, which drops the block at send time — that is what makes the warning true.
  '/premium_photo-1756066699592-eb346b61309b',
];

export function isDefaultPlaceholderUrl(url: string): boolean {
  try {
    const parsed = new URL(url, RELATIVE_URL_BASE);
    const { pathname } = parsed;
    if (STOCK_PATHNAME_PREFIXES.some(prefix => pathname.startsWith(prefix))) return true;
    if (parsed.searchParams.get('isDefault') === 'true') return true;
    if (STOCK_PATHNAMES.includes(pathname)) return true;
  } catch {
    // not parseable even against a base — treat as non-stock
  }
  return false;
}
